import ENV from "@/utils/env";
import { useEffect } from "react";
import { useGetAllAssetInsights } from "@/api/studio/insights/queries";
import { Card } from "@/components/ui/card";
import { NavButton } from "@/components/ui/navigation";
import { Tabs, Tab, TabPanel } from "@/components/ui/tabs";
import { ReportsLayout } from "@/components/layouts";
// import { Copilot } from "@/features/reports/components/copilot";
import { AssetRender } from "@/features/reports/components/in-depth/asset-render";
import { ReportInDepthSkeleton } from "@/features/reports/components/in-depth/report-in-depth-skeleton";
// import { CopilotButton } from "@/features/reports/components/co-pilot-button";
import { useSegment } from "@/hooks/use-segment";
import { useGlobalStore } from "@/stores/global-store";
import { useModalStore } from "@/stores/use-modal-store";
import { ReportType, useReportsContext } from "@/features/reports/contexts/reports-context";
import { useReportFiltersStore } from "@/features/reports/stores/use-report-filters-store";
import { CreativeImpactDetails } from "@/features/reports/components/in-depth/creative-impact-details";
import { AttentionDetails } from "@/features/reports/components/in-depth/attention-details";
import { EmotionDetails } from "@/features/reports/components/in-depth/emotion-details";
import { MemoryDetails } from "@/features/reports/components/in-depth/memory-details";
import { Copilot } from "@/features/reports/components/copilot";
import { EmotionUpsellModal } from "@/features/reports/modals/emotion-upsell-modal";
import { MemoryUpsellModal } from "@/features/reports/modals/memory-upsell-modal";

/* Prop Types */
interface ReportInDepthRouteProps {
  projectId: string;
  studyId: string;
}

/*
 * ReportInDepthRoute component
 *
 * This provides the route to deep dive into a specific asset within a study and switch between
 * different report types, metrics and visualisation options.
 */
export const ReportInDepthRoute = ({ projectId, studyId }: ReportInDepthRouteProps) => {
  /* Stores */
  const sortBy = useReportFiltersStore((state) => state.sortBy);
  const orderBy = useReportFiltersStore((state) => state.orderBy);

  /* Queries */
  const { data: allInsights, isLoading: isAllInsightsLoading } = useGetAllAssetInsights({ projectId, studyId, orderBy, sortBy });

  /* Context */
  const {
    state: { assetKey, contextKey, reportType, attentionMetric, showCopilot },
    actions: { setReportType, setAssetKey, setContextKey, setShowCopilot },
  } = useReportsContext();

  /* Stores */
  const theme = useGlobalStore((state) => state.theme);
  const entitlements = useGlobalStore((state) => state.entitlements);
  const { open } = useModalStore((state) => state.modalActions);

  /* Consts */
  const isMemoryEmotionEnabled = entitlements?.some((item) => item.name === "memoryEmotion");

  /* Hooks */
  const { segmentTrackEvent, segmentPageEvent } = useSegment();

  // select the current asset using the assetKey from context
  const currentAsset = allInsights?.find((insight) => insight.key === assetKey);

  // not every asset has context, so we need to check if the first asset has context as its set at a study level
  const hasContext = !!allInsights?.[0]?.metrics?.attention?.visibility;

  /* Handlers */
  const handleReportTypeChange = (reportType: ReportType) => {
    // Segment Tracking
    segmentTrackEvent("Asset Report Viewed", {
      assetId: assetKey,
      projectId,
      studyId,
      reportType: reportType,
      studyType: "asset",
    });

    // close the copilot when switching report types
    setShowCopilot(false);

    // set the reportType in context
    setReportType(reportType);
  };

  const handleAssetClick = (assetKey: string) => {
    // set the asset key in context
    setAssetKey(assetKey);
  };

  const handleEmotionUpsellModal = () => {
    open(<EmotionUpsellModal />);
  };

  const handleMemoryUpsellModal = () => {
    open(<MemoryUpsellModal />);
  };

  /* Side effects */
  useEffect(() => {
    // If no assetKey is set, default to the first asset in the list
    if (!assetKey && allInsights && allInsights.length > 0) {
      setAssetKey(allInsights[0].key);
    }
  }, [assetKey, allInsights]);

  useEffect(() => {
    if (!contextKey && hasContext && currentAsset && currentAsset.attention && currentAsset.attention.contexts) {
      // set context key to the first context in the list if there is no context key already set
      const contextKey = currentAsset.attention.contexts[0].key;
      if (contextKey) {
        setContextKey(contextKey);
      }
    }
  }, [contextKey, currentAsset, hasContext]);

  useEffect(() => {
    // segment page event when a user visits the report in depth page
    if (theme)
      segmentPageEvent("Asset Report", {
        title: "Asset Report | Dragonfly AI Studio",
        theme: theme,
      });
  }, [theme, segmentPageEvent]);

  /* Render */
  return (
    <ReportsLayout projectId={projectId} studyId={studyId} hideSidebar>
      {/* Skeleton Loader */}
      {isAllInsightsLoading && <ReportInDepthSkeleton uploadLength={3} />}

      {allInsights && !isAllInsightsLoading && currentAsset && (
        <>
          {/* Asset name */}
          <div className="flex flex-row items-center justify-between">
            <div className="flex flex-row items-center">
              <NavButton />
              <h2 className="font-semibold ml-2">{currentAsset?.assetName}</h2>
            </div>
          </div>

          {/* Thumbnails */}
          <div className="grow flex flex-row gap-4 max-h-full overflow-hidden">
            <div className="flex flex-col gap-4 overflow-y-auto min-w-[10vw] max-w-[10vw]">
              {allInsights?.map((insight) => (
                <Card
                  size="small"
                  key={insight.assetId}
                  onClick={() => handleAssetClick(insight.key)}
                  imgFit="contain"
                  imageUrl={`${ENV.API_PLATFORM_URL}/pipeline/renderer/images/v1?prefix=${projectId}/${studyId}&key=${insight.key}&maxWidth=100`}
                  className={`cursor-pointer hover:opacity-100 ${insight.key === assetKey ? "opacity-100 shadow-lg" : "opacity-60"}`}
                >
                  <p className="font-bold p-4 truncate">{insight.assetName}</p>
                </Card>
              ))}
            </div>

            {/* Image Viewer */}
            <AssetRender projectId={projectId} studyId={studyId} />

            {/* Insight Panel */}
            <div className="flex flex-col min-w-[35vw] max-w-[35vw] overflow-y-auto" style={{ scrollbarGutter: "stable" }}>
              <Tabs defaultTab={reportType} onTabChange={(tab) => handleReportTypeChange(tab as ReportType)} showActiveBg>
                {/* Tab */}
                <Tab value="creative" className="flex-1 h-11" style={{ borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }}>
                  Creative Impact
                </Tab>
                <Tab value="attention" className="flex-1 h-11" style={{ borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }}>
                  Attention
                </Tab>
                <Tab
                  value="emotion"
                  className="flex-1 h-11"
                  style={{ borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }}
                  onClick={!isMemoryEmotionEnabled ? handleEmotionUpsellModal : undefined}
                  disabled={!isMemoryEmotionEnabled}
                >
                  Emotion
                </Tab>
                <Tab
                  value="memory"
                  className="flex-1 h-11"
                  style={{ borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }}
                  onClick={!isMemoryEmotionEnabled ? handleMemoryUpsellModal : undefined}
                  disabled={!isMemoryEmotionEnabled}
                >
                  Memory
                </Tab>

                {/* Creative Panel */}
                <TabPanel value="creative">
                  <CreativeImpactDetails currentAsset={currentAsset} />
                </TabPanel>

                {/* Attention Panel */}
                <TabPanel
                  value="attention"
                  className="border-t border-base-200 border-l-0 border-r-0 border-b-0 rounded-l-0"
                  style={{ borderStartStartRadius: "0", borderEndStartRadius: "0" }}
                >
                  <AttentionDetails currentAsset={currentAsset} hasContext={hasContext} studyId={studyId} projectId={projectId} />
                </TabPanel>

                {/* Emotion Panel */}
                <TabPanel
                  value="emotion"
                  className="border-t border-base-200 border-l-0 border-r-0 border-b-0 rounded-l-0"
                  style={{ borderStartStartRadius: "0", borderEndStartRadius: "0" }}
                >
                  <EmotionDetails currentAsset={currentAsset} studyId={studyId} projectId={projectId} />
                </TabPanel>

                {/* Memory Panel */}
                <TabPanel
                  value="memory"
                  className="border-t border-base-200 border-l-0 border-r-0 border-b-0 rounded-l-0"
                  style={{ borderStartStartRadius: "0", borderEndStartRadius: "0" }}
                >
                  <MemoryDetails currentAsset={currentAsset} studyId={studyId} projectId={projectId} />
                </TabPanel>
              </Tabs>
            </div>

            {/* Copilot */}
            {showCopilot && <Copilot projectId={projectId} studyId={studyId} assetId={currentAsset.key} reportType={reportType} metric={attentionMetric} />}
          </div>
        </>
      )}
    </ReportsLayout>
  );
};
